import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

export type MissingDefaultChannel = {
  channelId: string;
  channelTitle?: string;
};

type Props = {
  visible: boolean;
  onHide: () => void;
  channels: MissingDefaultChannel[];
  defaultBannersPath: string;
  /** When set, overrides the default header and intro copy. */
  reason?: "missing" | "invalid";
};

const INTRO_COPY: Record<NonNullable<Props["reason"]>, string> = {
  missing:
    "Each channel needs a saved default banner before you can create a swap. The end phase restores that default. Upload a full-resolution file (at least 2048×1152, 16:9, max 6MB; 2560×1440 recommended). Accepting the live YouTube banner often saves a preview-sized image that will fail at revert.",
  invalid:
    "The saved default banner for these channels does not meet YouTube upload requirements (min 2048×1152, 16:9, max 6MB). Upload a full-resolution default on the Default banners page before creating or continuing a swap.",
};

const MissingDefaultBannerDialog: React.FC<Props> = ({
  visible,
  onHide,
  channels,
  defaultBannersPath,
  reason = "missing",
}) => {
  const navigate = useNavigate();

  const handleGoToDefaultBanners = () => {
    onHide();
    navigate(defaultBannersPath);
  };

  return (
    <Dialog
      header={
        reason === "invalid"
          ? "Default banner not upload-ready"
          : "Default banner required"
      }
      visible={visible}
      onHide={onHide}
      style={{ width: "min(520px, 92vw)" }}
      closable
      footer={
        <div className="flex justify-end gap-2">
          <Button label="Cancel" severity="secondary" onClick={onHide} />
          <Button
            label="Go to Default banners"
            icon="pi pi-arrow-right"
            onClick={handleGoToDefaultBanners}
          />
        </div>
      }
    >
      <p className="text-sm text-gray-700 mb-3 leading-relaxed">
        {INTRO_COPY[reason]}
      </p>
      <ul className="text-sm text-gray-800 list-disc pl-5 space-y-1 max-h-48 overflow-y-auto">
        {channels.map((channel) => (
          <li key={channel.channelId}>
            <span className="font-medium">
              {channel.channelTitle?.trim() || channel.channelId}
            </span>
            {channel.channelTitle?.trim() ? (
              <span className="text-gray-500 font-mono text-xs ml-1">
                ({channel.channelId})
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </Dialog>
  );
};

export default MissingDefaultBannerDialog;
